import { create } from 'zustand';
import { Conversation, ConversationMessage } from '../types';
import { conversationApi } from '../api/conversations';
import { notificationApi } from '../api/notifications';

interface ConversationState {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  activeAgentId?: string;
  messages: ConversationMessage[];
  loading: boolean;
  messagesLoading: boolean;
  hasMoreMessages: boolean;
  messagePage: number;
  unreadCounts: Record<string, number>;
  /** Most recent id passed to `selectConversation`. Used as a stale-
   * response guard so concurrent fetches for an older conversation
   * (polling refreshes, racing selects) can't overwrite the current
   * view's messages. */
  pendingConversationId: string | null;

  fetchConversations: (chatType?: 'human-bot' | 'bot-bot', agentId?: string) => Promise<void>;
  selectConversation: (id: string, agentId?: string) => Promise<void>;
  /** Set a fully-formed conversation object without going through the
   * API. Used by surfaces like the synthesized "default-team" group
   * which has no backend record but should still drive the detail
   * page like a real conversation. */
  setLocalConversation: (conv: Conversation, agentId?: string) => void;
  fetchMessages: (id: string, agentId?: string) => Promise<void>;
  loadMoreMessages: (agentId?: string) => Promise<void>;
  refreshMessages: (agentId?: string) => Promise<void>;
  markRead: (id: string, agentId?: string) => Promise<void>;
  fetchUnreadCounts: () => Promise<void>;
}

export const useConversationStore = create<ConversationState>((set, get) => ({
  conversations: [],
  activeConversation: null,
  activeAgentId: undefined,
  messages: [],
  loading: false,
  messagesLoading: false,
  hasMoreMessages: true,
  messagePage: 1,
  unreadCounts: {},
  pendingConversationId: null,

  fetchConversations: async (chatType?: 'human-bot' | 'bot-bot', agentId?: string) => {
    set({ loading: true });
    try {
      const result = await conversationApi.getConversations(chatType, agentId);
      // Sync unread counts from conversation list response
      const unreadCounts: Record<string, number> = {};
      for (const conv of result.items) {
        if ((conv.unreadCount ?? 0) > 0) {
          unreadCounts[conv.id] = conv.unreadCount ?? 0;
        }
      }
      set({ conversations: result.items, loading: false, unreadCounts });
    } catch (error) {
      set({ loading: false });
      console.error('Failed to fetch conversations:', error);
    }
  },

  selectConversation: async (id: string, agentId?: string) => {
    // Clear stale state immediately so the previous conversation's
    // header / messages don't bleed into the new view while the new
    // detail + messages requests are in flight. Stamp `pendingConversationId`
    // so concurrent in-flight responses (selectConversation called
    // again, or a polling refreshMessages) can detect they're stale.
    set({
      activeConversation: null,
      messages: [],
      hasMoreMessages: false,
      messagePage: 1,
      messagesLoading: true,
      activeAgentId: agentId,
      pendingConversationId: id,
    });
    try {
      const [conv, msgs] = await Promise.all([
        conversationApi.getDetail(id, agentId),
        conversationApi.getMessages(id, 1, 20, agentId),
      ]);
      // If the user navigated to a different conversation while
      // these requests were running, drop the result on the floor.
      if (get().pendingConversationId !== id) return;
      set({
        activeConversation: conv,
        activeAgentId: agentId,
        messages: msgs.items,
        hasMoreMessages: msgs.hasMore,
        messagePage: 1,
        messagesLoading: false,
      });
      // Mark as read when selecting
      get().markRead(id, agentId).catch(() => {});
    } catch (error) {
      if (get().pendingConversationId === id) {
        set({ messagesLoading: false });
      }
      console.error('Failed to select conversation:', error);
    }
  },

  setLocalConversation: (conv: Conversation, agentId?: string) => {
    // Drop into the same shape selectConversation produces but without
    // any network or polling — used for synthesized conversations like
    // the default user-team group that have no backend record.
    set({
      activeConversation: conv,
      activeAgentId: agentId,
      messages: [],
      hasMoreMessages: false,
      messagePage: 1,
      messagesLoading: false,
      pendingConversationId: conv.id,
    });
  },

  markRead: async (id: string, agentId?: string) => {
    try {
      await conversationApi.markRead(id, agentId);
      set((state) => ({
        unreadCounts: { ...state.unreadCounts, [id]: 0 },
        conversations: state.conversations.map((c) =>
          c.id === id ? { ...c, unreadCount: 0 } : c
        ),
      }));
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
    }
  },

  fetchUnreadCounts: async () => {
    try {
      const result = await notificationApi.getUnreadCount();
      const unreadCounts: Record<string, number> = {};
      for (const item of result.conversationUnreads ?? []) {
        unreadCounts[item.conversationId] = item.unreadCount;
      }
      set({ unreadCounts });
    } catch (error) {
      console.error('Failed to fetch unread counts:', error);
    }
  },

  fetchMessages: async (id: string, agentId?: string) => {
    set({ messagesLoading: true });
    try {
      const msgs = await conversationApi.getMessages(id, 1, 20, agentId);
      set({
        activeAgentId: agentId,
        messages: msgs.items,
        hasMoreMessages: msgs.hasMore,
        messagePage: 1,
        messagesLoading: false,
      });
    } catch (error) {
      set({ messagesLoading: false });
      console.error('Failed to fetch messages:', error);
    }
  },

  loadMoreMessages: async (agentId?: string) => {
    const { activeConversation, activeAgentId, messagePage, hasMoreMessages, messagesLoading } = get();
    if (!activeConversation || !hasMoreMessages || messagesLoading) return;
    set({ messagesLoading: true });
    try {
      const nextPage = messagePage + 1;
      const resolvedAgentId = agentId || activeAgentId;
      const msgs = await conversationApi.getMessages(activeConversation.id, nextPage, 20, resolvedAgentId);
      set((state) => ({
        activeAgentId: resolvedAgentId,
        messages: [...state.messages, ...msgs.items],
        hasMoreMessages: msgs.hasMore,
        messagePage: nextPage,
        messagesLoading: false,
      }));
    } catch (error) {
      set({ messagesLoading: false });
      console.error('Failed to load more messages:', error);
    }
  },

  refreshMessages: async (agentId?: string) => {
    const { activeConversation, activeAgentId } = get();
    if (!activeConversation) return;
    const targetId = activeConversation.id;
    set({ messagesLoading: true });
    try {
      const resolvedAgentId = agentId || activeAgentId;
      const msgs = await conversationApi.getMessages(targetId, 1, 20, resolvedAgentId);
      // If the user navigated away while polling was in flight,
      // discard the response so we don't overwrite the new view.
      if (get().activeConversation?.id !== targetId) return;
      set({
        activeAgentId: resolvedAgentId,
        messages: msgs.items,
        hasMoreMessages: msgs.hasMore,
        messagePage: 1,
        messagesLoading: false,
      });
    } catch (error) {
      if (get().activeConversation?.id === targetId) {
        set({ messagesLoading: false });
      }
      console.error('Failed to refresh messages:', error);
    }
  },
}));
